import { useEffect, useState } from "react";
import type { SetURLSearchParams } from "react-router-dom";
import { THROUGHLINE_WINDOW_OPTIONS, type ThroughlineWindowDays } from "@/lib/throughlinePresentationConfig";

type UseThroughlineSelectionSyncInput = {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
};

export function useThroughlineSelectionSync({
  searchParams,
  setSearchParams,
}: UseThroughlineSelectionSyncInput) {
  const supportedWindowParams = THROUGHLINE_WINDOW_OPTIONS.map(String);
  const requestedWindowFromUrl = searchParams.get("window");
  const parsedWindowFromUrl = requestedWindowFromUrl && supportedWindowParams.includes(requestedWindowFromUrl)
    ? (Number(requestedWindowFromUrl) as ThroughlineWindowDays)
    : null;

  const [throughlineWindowDays, setThroughlineWindowDays] = useState<ThroughlineWindowDays>(parsedWindowFromUrl || 15);
  const [selectedThroughlineDay, setSelectedThroughlineDay] = useState<string | null>(searchParams.get("day"));

  useEffect(() => {
    if (!parsedWindowFromUrl) return;
    setThroughlineWindowDays((current) => (current === parsedWindowFromUrl ? current : parsedWindowFromUrl));
  }, [parsedWindowFromUrl]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("window", String(throughlineWindowDays));
    if (selectedThroughlineDay) {
      nextParams.set("day", selectedThroughlineDay);
    } else {
      nextParams.delete("day");
    }
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, selectedThroughlineDay, setSearchParams, throughlineWindowDays]);

  return {
    throughlineWindowDays,
    setThroughlineWindowDays,
    selectedThroughlineDay,
    setSelectedThroughlineDay,
  };
}
